import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

import numeral from 'numeral'

interface CounterpartyFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    managers: any[];
    targetForDelete: any[];
    form: any;
    schema: any;

    meta: any;
}

const state: CounterpartyFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    managers: [],
    targetForDelete: [],
    form: null,
    schema: null,

    meta: {},
};

const initState: CounterpartyFormState = cloneDeep(state);

export const DELIVERY_ZONE_DATA = 'GET_DELIVERY_ZONE_DATA';
export const DELIVERY_ZONE_MANAGERS_DATA = 'GET_DELIVERY_ZONE_MANAGERS_DATA';
export const DELIVERY_ZONE_SCHEMA = 'GET_DELIVERY_ZONE_SCHEMA';

export const actions: ActionTree<CounterpartyFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(DELIVERY_ZONE_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(DELIVERY_ZONE_MANAGERS_DATA)
                    .raw('documents.list')
                    .action({
                        columns: {
                          id: 'dzgm.id',
                          manager_id: 'dzgm.manager_id',
                          name: 'e.name'
                        },
                        from: `s:delivery_zone_groups_managers`,
                        from_as: 'dzgm',
                        joins: [
                            {
                                join: 's:employees',
                                as: 'e',
                                on: {
                                    $eq: ['dzgm.manager_id', 'e.id']
                                }
                            }
                        ],
                        where: {
                          $eq: [`dzgm.delivery_zone_group_id`, {$: params.id}]
                        }
                    }),
            ]).then((result: ServerResult) => {
                const opResDeliveryZone = result.getOperation(DELIVERY_ZONE_DATA).getContent()
                const opResDeliveryZoneManages = result.getOperation(DELIVERY_ZONE_MANAGERS_DATA).getContent()
                commit('SET_FORM_DATA', {
                    ...opResDeliveryZone,
                    managers: opResDeliveryZoneManages.documents
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(DELIVERY_ZONE_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:delivery_zone_groups`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(DELIVERY_ZONE_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;
            const managers = state.managers.map((item: any) => {
                return {
                    id: item.id,
                    manager_id: item.manager_id
                }
            });


            if (documentId) {
                saveQuery = vm.$q('DELIVERY_ZONE_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('delivery_zone_groups_managers', 'delivery_zone_group_id', (createRelated: any) => {
                      return managers.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item, index) => createRelated(item));
                    })
                    .deleteManyRelation('delivery_zone_groups_managers', 'delivery_zone_group_id', (deleteRelated: any) => {
                      return state.targetForDelete.map((item: any, index) => deleteRelated({id: item}));
                    })
            } else {
                saveQuery = vm.$q('DELIVERY_ZONE_DATA_SAVE')
                    .create('s:delivery_zone_groups', normalizedForm)
                    .withManyRelation('delivery_zone_groups_managers', 'delivery_zone_group_id', (createRelated: any) => {
                      return managers.map((item: any) => createRelated(item));
                    })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('DELIVERY_ZONE_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    addManager({commit}, manager) {
        commit('ADD_MANAGER', manager);
    },
  
    removeManager({commit}, index: number) {
        commit('REMOVE_MANAGER', index);
    },

};

export const mutations: MutationTree<CounterpartyFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema, managers}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document, true);
        state.managers = managers
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: CounterpartyFormState, {field, newValue}) {
        switch (field) {
            case 'delivery_delay':
                state.form[field] = numeral(newValue.replace(',', '.')).value();
                break;

            default:
                state.form[field] = newValue;
        }
    },
    ADD_MANAGER(state, manager) {
      state.managers.push({
        manager_id: manager.id,
        name: manager.label
      })
    },
    REMOVE_MANAGER(state, index) {
        console.log(state.managers)
        if (state.managers[index].id) {
          state.targetForDelete.push(state.managers[index].id)
        }
    
        state.managers.splice(index, 1);
    }
};

const getters = {
    fields: (state: CounterpartyFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_delivery_zone_group_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
